import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { HttpErrorResponse, HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import { SharedApiService, ApiType } from './api.service';
import { AttachmentService } from './attachment.service';
import { enums } from '../models/dashboard/job-offer/job-offer-enums';

export interface ParseCvPersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  address: string;
  city: string;
  houseNumber: string;
}

export interface ParseCvRequest {
  fileType: string;
  fileExt: string;
  fileUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  address: string;
  city: string;
  houseNumber: string;
  anonymize: boolean;
  provider: string;
  lang: string;
}

@Injectable({
  providedIn: 'root',
})
export class CvParsingService {
  // Observable subjects for parsing status
  parsingInProgress$ = new Subject<boolean>();
  parsingComplete$ = new Subject<boolean>();
  parsingSuccess$ = new Subject<boolean>();
  parsingError$ = new Subject<string>();

  // Observable that components can subscribe to for parsed data
  resumeDataParsed$ = new Subject<any>();

  // Track if CV parsing is complete
  private _isParsed = false;
  get isParsed(): boolean {
    return this._isParsed || localStorage.getItem('parsedResumeData') !== null;
  }

  constructor(private sharedApiService: SharedApiService, private attachmentService: AttachmentService, private http: HttpClient) {}

  /**
   * Parse a CV file using AI
   * @param file The CV file to parse
   * @param personalInfo Personal info context for parsing
   * @param aiParsingConsent Consent for AI parsing
   * @param lang Language for parsing
   * @returns Observable with the parsing result
   */
  parseCvFile(file: File, personalInfo: ParseCvPersonalInfo, aiParsingConsent: boolean, lang: string, jobsRequest?: boolean): Observable<any> {
    if (!file) {
      return throwError(() => new Error('No file provided'));
    }

    // Validate required personal info fields
    if (!personalInfo.firstName || personalInfo.firstName.trim() === '') {
      return throwError(() => new Error('First name is required for CV parsing'));
    }
    if (!personalInfo.lastName || personalInfo.lastName.trim() === '') {
      return throwError(() => new Error('Last name is required for CV parsing'));
    }
    if (!personalInfo.email || personalInfo.email.trim() === '') {
      return throwError(() => new Error('Email is required for CV parsing'));
    }
    if (!personalInfo.address || personalInfo.address.trim() === '') {
      return throwError(() => new Error('Address is required for CV parsing'));
    }
    if (!personalInfo.city || personalInfo.city.trim() === '') {
      return throwError(() => new Error('City is required for CV parsing'));
    }
    if (!personalInfo.houseNumber || String(personalInfo.houseNumber).trim() === '') {
      return throwError(() => new Error('House number is required for CV parsing'));
    }

    this.parsingInProgress$.next(true);

    // Get file extension
    const fileExt = file.name.split('.').pop() || '';

    // Create FormData with the actual file
    // Wrap file in Blob with application/octet-stream to avoid content type validation issues
    const fileBlob = new Blob([file], { type: 'application/octet-stream' });
    const formData = new FormData();
    formData.append('file', fileBlob, file.name); // Send as octet-stream
    formData.append('fileType', file.type); // Keep original type in parameter
    formData.append('fileExt', fileExt);
    formData.append('firstName', personalInfo.firstName);
    formData.append('lastName', personalInfo.lastName);
    formData.append('email', personalInfo.email);
    formData.append('phone', personalInfo.phone || '');
    formData.append('DOB', personalInfo.dob || '');
    formData.append('address', personalInfo.address || '');
    formData.append('city', personalInfo.city || '');
    formData.append('houseNumber', personalInfo.houseNumber || '');
    formData.append('anonymize', 'true');
    formData.append('provider', 'mistral');
    formData.append('lang', lang || 'en');
    formData.append('aiParsingConsent', aiParsingConsent.toString());

    // Get headers without Content-Type (let browser set multipart/form-data)
    const headers = this.sharedApiService.getHTTPHeaders(jobsRequest).delete('Content-Type');
    const url = this.sharedApiService.getApiUrl('api/parse-cv', ApiType.OPEN);

    return new Observable<any>((observer) => {
      this.http.post<any>(url, formData, { headers }).subscribe({
        next: (response) => {
          // Clear existing parsed data before adding new parsed data
          this.clearParsedResumeData();
          this.parseAndAutofillResume(response.result);
          this.parsingSuccess$.next(true);
          observer.next(response);
          observer.complete();
        },
        error: (error: HttpErrorResponse) => {
          console.error('CvParsingService: Error during CV parsing:', error);

          // For rate limit errors, pass the full HttpErrorResponse so component can extract details
          if (error.status === 429) {
            this.parsingError$.next('Rate limit exceeded');
            observer.error(error);
            return;
          }

          let errorMessage = 'An error occurred during parsing.';
          if (error.error && typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.parsingError$.next(errorMessage);
          observer.error(errorMessage);
        },
        complete: () => {
          this.parsingInProgress$.next(false);
        },
      });
    });
  }

  /**
   * Process and store parsed resume data
   * @param parsedData The parsed data from the API
   */
  parseAndAutofillResume(parsedData: any): void {
    // Validate and clean the parsed data before storing
    const cleanedData = this.validateAndCleanParsedData(parsedData);

    localStorage.setItem('parsedResumeData', JSON.stringify(cleanedData));

    // Store timestamp when parsing is completed for autosave timing
    localStorage.setItem('cvParsingTimestamp', Date.now().toString());

    // Set autofill flags for sections with data
    const autofillSections = {
      personalInfo: cleanedData.personalInfo ? true : false,
      highSchool: cleanedData.highSchool && cleanedData.highSchool.length > 0,
      university: cleanedData.university && cleanedData.university.length > 0,
      languages: cleanedData.languages && cleanedData.languages.length > 0,
      workExperiences: cleanedData.workExperiences && cleanedData.workExperiences.length > 0,
      projects: cleanedData.projects && cleanedData.projects.length > 0,
      skills: cleanedData.skills && cleanedData.skills.length > 0,
      driversLicenses: cleanedData.driversLicenses && cleanedData.driversLicenses.length > 0,
    };

    localStorage.setItem('autofillSections', JSON.stringify(autofillSections));

    // Store timestamp when parsing completes to prevent autosave on page reloads
    localStorage.setItem('cvParsingTimestamp', Date.now().toString());

    // Set parsing completion flag
    this._isParsed = true;
    this.parsingComplete$.next(true);

    // Notify components that data is ready
    this.resumeDataParsed$.next(autofillSections);
  }

  /**
   * Validates and cleans parsed CV data by removing placeholder values and invalid options
   * @param parsedData The raw parsed data from CV parsing
   * @returns Cleaned and validated data
   */
  private validateAndCleanParsedData(parsedData: any): any {
    const cleanedData = { ...parsedData };

    // Clean personal info
    if (cleanedData.personalInfo) {
      cleanedData.personalInfo = this.cleanPersonalInfo(cleanedData.personalInfo);
    }

    // Clean skills
    if (cleanedData.skills && Array.isArray(cleanedData.skills)) {
      cleanedData.skills = cleanedData.skills.map((skill: any) => this.cleanSkill(skill)).filter((skill: any) => skill !== null);
    }

    // Clean languages
    if (cleanedData.languages && Array.isArray(cleanedData.languages)) {
      cleanedData.languages = cleanedData.languages.map((language: any) => this.cleanLanguage(language)).filter((language: any) => language !== null);
    }

    // Clean work experiences
    if (cleanedData.workExperiences && Array.isArray(cleanedData.workExperiences)) {
      cleanedData.workExperiences = cleanedData.workExperiences.map((work: any) => this.cleanWorkExperience(work)).filter((work: any) => work !== null);
    }

    // Clean university education
    if (cleanedData.university && Array.isArray(cleanedData.university)) {
      cleanedData.university = cleanedData.university.map((uni: any) => this.cleanUniversityEducation(uni)).filter((uni: any) => uni !== null);
    }

    // Clean high school education
    if (cleanedData.highSchool && Array.isArray(cleanedData.highSchool)) {
      cleanedData.highSchool = cleanedData.highSchool.map((hs: any) => this.cleanHighSchoolEducation(hs)).filter((hs: any) => hs !== null);
    }

    // Clean projects
    if (cleanedData.projects && Array.isArray(cleanedData.projects)) {
      cleanedData.projects = cleanedData.projects.map((project: any) => this.cleanProject(project)).filter((project: any) => project !== null);
    }

    // Clean drivers licenses
    if (cleanedData.driversLicenses && Array.isArray(cleanedData.driversLicenses)) {
      cleanedData.driversLicenses = this.cleanDriversLicenses(cleanedData.driversLicenses);
    }

    return cleanedData;
  }

  /**
   * Checks if a value is a placeholder (e.g., <CITY>, <n>, etc.) or contains placeholders
   */
  private isPlaceholderValue(value: string): boolean {
    if (!value || typeof value !== 'string') return false;
    return /<[A-Z_]+>/.test(value) || value.includes('<') || value.includes('>');
  }

  /**
   * Removes embedded placeholders from a string while preserving the rest of the text
   */
  private removePlaceholders(value: string): string {
    if (!value || typeof value !== 'string') return '';
    return value.replace(/<[^>]*>/g, '').trim();
  }

  /**
   * Checks if a value is empty, null, undefined or only whitespace
   */
  private isEmpty(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  /**
   * Cleans personal info by removing placeholder values
   */
  private cleanPersonalInfo(personalInfo: any): any {
    if (!personalInfo) return null;

    const cleaned = { ...personalInfo };
    const fieldsToCheck = ['firstName', 'lastName', 'email', 'phone', 'address', 'city'];

    fieldsToCheck.forEach((field) => {
      if (this.isEmpty(cleaned[field])) {
        cleaned[field] = '';
      } else if (this.isPlaceholderValue(cleaned[field])) {
        // If the entire value is a placeholder, clear it
        if (/^<[A-Z_]+>$/.test(cleaned[field]?.trim())) {
          cleaned[field] = '';
        } else {
          // If it contains embedded placeholders, remove them
          cleaned[field] = this.removePlaceholders(cleaned[field]);
        }
      }
    });

    return cleaned;
  }

  /**
   * Cleans a skill entry by validating skill name and level
   */
  private cleanSkill(skill: any): any {
    if (!skill || typeof skill !== 'object') return null;

    const cleaned = { ...skill };

    // Clean skill name
    if (this.isEmpty(cleaned.skillName) || this.isPlaceholderValue(cleaned.skillName)) {
      return null; // Skip invalid skills
    }

    // Clean skill level
    if (this.isEmpty(cleaned.level) || this.isPlaceholderValue(cleaned.level)) {
      cleaned.level = 'Beginner'; // Default level
    }

    return cleaned;
  }

  /**
   * Cleans a language entry by validating language and level
   */
  private cleanLanguage(language: any): any {
    if (!language || typeof language !== 'object') return null;

    const cleaned = { ...language };

    // Clean language name
    if (this.isEmpty(cleaned.language) || this.isPlaceholderValue(cleaned.language)) {
      return null; // Skip invalid languages
    }

    // Clean language level
    if (this.isEmpty(cleaned.level) || this.isPlaceholderValue(cleaned.level)) {
      cleaned.level = 'Beginner'; // Default level
    }

    return cleaned;
  }

  /**
   * Cleans work experience entries
   */
  private cleanWorkExperience(work: any): any {
    if (!work || typeof work !== 'object') return null;

    const cleaned = { ...work };

    // Check required fields
    if (this.isEmpty(cleaned.position) || this.isPlaceholderValue(cleaned.position)) {
      return null; // Skip entries without position
    }

    if (this.isEmpty(cleaned.employer) || this.isPlaceholderValue(cleaned.employer)) {
      return null; // Skip entries without employer
    }

    // Clean optional fields
    ['city', 'country'].forEach((field) => {
      if (this.isPlaceholderValue(cleaned[field])) {
        cleaned[field] = '';
      }
    });

    return cleaned;
  }

  /**
   * Cleans university education entries
   */
  private cleanUniversityEducation(uni: any): any {
    if (!uni || typeof uni !== 'object') return null;

    const cleaned = { ...uni };

    // Check required fields
    if (this.isEmpty(cleaned.universityName) || this.isPlaceholderValue(cleaned.universityName)) {
      return null;
    }

    if (this.isEmpty(cleaned.fieldOfStudy) || this.isPlaceholderValue(cleaned.fieldOfStudy)) {
      return null;
    }

    // Clean optional fields
    ['city', 'country'].forEach((field) => {
      if (this.isPlaceholderValue(cleaned[field])) {
        cleaned[field] = '';
      }
    });

    return cleaned;
  }

  /**
   * Cleans high school education entries
   */
  private cleanHighSchoolEducation(hs: any): any {
    if (!hs || typeof hs !== 'object') return null;

    const cleaned = { ...hs };

    // Check required fields
    if (this.isEmpty(cleaned.highSchoolName) || this.isPlaceholderValue(cleaned.highSchoolName)) {
      return null;
    }

    if (this.isEmpty(cleaned.fieldOfStudy) || this.isPlaceholderValue(cleaned.fieldOfStudy)) {
      return null;
    }

    // Clean optional fields
    ['city', 'country'].forEach((field) => {
      if (this.isPlaceholderValue(cleaned[field])) {
        cleaned[field] = '';
      }
    });

    return cleaned;
  }

  /**
   * Cleans project entries
   */
  private cleanProject(project: any): any {
    if (!project || typeof project !== 'object') return null;

    const cleaned = { ...project };

    // Check required fields
    if (this.isEmpty(cleaned.projectName) || this.isPlaceholderValue(cleaned.projectName)) {
      return null;
    }

    // Clean optional fields
    ['description', 'technologies', 'url'].forEach((field) => {
      if (this.isPlaceholderValue(cleaned[field])) {
        cleaned[field] = '';
      }
    });

    return cleaned;
  }

  /**
   * Valid driver's license types
   */
  private readonly VALID_LICENSE_TYPES = enums.driversLicenseTypes;

  /**
   * Cleans drivers licenses by validating against allowed types
   */
  private cleanDriversLicenses(licenses: any[]): string[] {
    if (!licenses || !Array.isArray(licenses)) return [];

    return licenses
      .map((license: any) => {
        // Handle both string and object formats
        const licenseValue = typeof license === 'string' ? license : license?.type || license?.license || '';
        return licenseValue.toUpperCase().trim();
      })
      .filter((license: string) => {
        // Only include valid license types
        return license && !this.isPlaceholderValue(license) && this.VALID_LICENSE_TYPES.includes(license);
      });
  }

  /**
   * Get parsed resume data from localStorage
   */
  getParsedResumeData(): any {
    const data = localStorage.getItem('parsedResumeData');
    return data ? JSON.parse(data) : null;
  }

  /**
   * Clear parsed resume data
   */
  clearParsedResumeData(): void {
    localStorage.removeItem('parsedResumeData');
    localStorage.removeItem('autofillSections');
    localStorage.removeItem('cvParsingTimestamp');
    this._isParsed = false;
    this.parsingComplete$.next(false);
  }
}
